// Table-only action targets and collection context; the shared action engine stays unchanged.
export function targetValue(rule) {
  return JSON.stringify([rule.field || '', rule.elementKey || ''])
}

export function actionTargetOptions(params, rules = []) {
  const options = [{ text: 'Toute la ligne', field: 'all', elementKey: '' }]
  if (Array.isArray(params.colonnes) && params.colonnes.length) {
    params.colonnes.forEach(column => {
      if (!column || !column.key) return
      const title = column.title || column.key
      options.push({ text: title + ' · colonne', field: column.key, elementKey: '' })
      const walk = nodes => (Array.isArray(nodes) ? nodes : []).forEach(node => {
        if (!node) return
        if (node.key) options.push({ text: title + ' › ' + node.key + ' · ' + node.type, field: column.key, elementKey: node.key })
        walk(node.content)
      })
      walk(column.content)
    })
  } else {
    (params.champs || []).forEach(field => {
      if (field && field.display === true && field.as && !['cellBackColor', 'rowColor', 'rowBackColor'].includes(field.as)) {
        options.push({ text: field.as, field: field.as, elementKey: '' })
      }
    })
  }
  const seen = new Set()
  const result = []
  options.forEach(option => {
    const value = targetValue(option)
    if (!seen.has(value)) { seen.add(value); result.push({ ...option, value }) }
    else {
      const existing = result.find(entry => entry.value === value)
      if (existing && !existing.disabled) { existing.disabled = true; existing.text += ' (clé ambiguë)' }
    }
  })
  rules.forEach(rule => {
    const value = targetValue(rule)
    if (rule.field && !seen.has(value)) {
      seen.add(value)
      result.push({ text: [rule.field, rule.elementKey].filter(Boolean).join(' › ') + ' (cible indisponible)', value, field: rule.field, elementKey: rule.elementKey || '' })
    }
  })
  return result
}

export function visualKeyCount(column, key) {
  const count = nodes => (Array.isArray(nodes) ? nodes : []).reduce((total, node) => total + (node?.key === key ? 1 : 0) + count(node?.content), 0)
  return count(column?.content)
}

function pathValue(source, path) {
  if (source === null || source === undefined) return undefined
  if (['__proto__', 'prototype', 'constructor'].includes(path)) return undefined
  if (Object.prototype.hasOwnProperty.call(Object(source), path)) return source[path]
  return String(path).split(/[. ]/).filter(Boolean).reduce((value, key) => {
    if (['__proto__', 'prototype', 'constructor'].includes(key)) return undefined
    return value !== null && value !== undefined && Object.prototype.hasOwnProperty.call(Object(value), key) ? value[key] : undefined
  }, source)
}

export function resolveContextValue(value, context) {
  if (typeof value !== 'string') return value
  const resolve = (scope, path) => {
    if (scope === 'item' && path === 'index') return context.itemData?.__index
    if (scope === 'item' && path === 'value') {
      const item = context.itemData
      return item && typeof item === 'object' && Object.prototype.hasOwnProperty.call(item, 'value') ? item.value : item
    }
    return pathValue(scope === 'item' ? context.itemData : context.line, path)
  }
  const exact = value.match(/^\[\[(item|table|line)\|\|([^\]]+)\]\]$/)
  if (exact) {
    const resolved = resolve(exact[1], exact[2])
    return resolved === undefined ? '' : resolved
  }
  const local = value.replace(/\[\[(item|table|line)\|\|([^\]]+)\]\]/g, (match, scope, path) => {
    const resolved = resolve(scope, path)
    return resolved === null || resolved === undefined ? '' : String(resolved)
  })
  return context.replaceBy ? context.replaceBy({ text: local, page: context.page, line: context.line }) : local
}

function resolveTree(value, context) {
  if (Array.isArray(value)) return value.map(child => resolveTree(child, context))
  if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).map(([key, child]) => [key, resolveTree(child, context)]))
  return resolveContextValue(value, context)
}

export function contextualActionArguments(action, context) {
  const params = {}
  const query = {}
  const entryValue = entry => {
    if (entry.typedata === 'array') {
      let children = entry.value
      if (typeof children === 'string') {
        try { children = JSON.parse(children) }
        catch (error) {
          children = resolveContextValue(children, context)
          if (typeof children === 'string') { try { children = JSON.parse(children) } catch (error) { /* Keep a non-JSON value unchanged. */ } }
        }
      }
      if (Array.isArray(children) && children.every(child => child && typeof child === 'object' && 'key' in child)) {
        return Object.fromEntries(children.map(child => [child.key, entryValue(child)]))
      }
      return children
    }
    return entry.type === 'table' ? pathValue(context.line, entry.value) : resolveContextValue(entry.value, context)
  }
  ;(action.get || []).forEach(entry => {
    if (['__proto__', 'prototype', 'constructor'].includes(entry.key)) return
    const target = entry.typevar === 'query' ? query : params
    target[entry.key] = entryValue(entry)
  })
  return { params, query, setactivtab: resolveTree(action.params?.setactivtab || [], context), url: resolveContextValue(action.params?.apilink?.url || '', context) }
}

export function contextualActionAllowed(conditions, context) {
  if (!Array.isArray(conditions)) return false
  const test = condition => {
    if (!condition || typeof condition !== 'object') return false
    if (condition.type === 'groupcond') {
      const children = Array.isArray(condition.content) ? condition.content : []
      if (!children.length) return false
      return condition.andor === 'or' ? children.some(test) : condition.andor === 'and' && children.every(test)
    }
    if (condition.type !== 'value') return false
    const left = resolveContextValue(condition.variable, context)
    const right = resolveContextValue(condition.value, context)
    const numeric = value => value !== '' && value !== null && value !== undefined && Number.isFinite(Number(value))
    switch (condition.condition) {
      case 'value': return left === right
      case 'isequal': return left == right
      case '!=': return left != right
      case 'like': return String(left ?? '').includes(String(right ?? ''))
      case '*': return left !== '' && left !== null && left !== undefined
      case 'empty': return left === ''
      case 'null': return left === null || left === 'null'
      case 'istrue': return left === true
      case 'isfalse': return left === false
      case 'issup': return numeric(left) && numeric(right) && Number(left) > Number(right)
      case 'isinf': return numeric(left) && numeric(right) && Number(left) < Number(right)
      default: return false
    }
  }
  return conditions.every(test)
}
